import type React from 'react';
import { useEffect, useState } from 'react';
import { Trans, useTranslation } from 'react-i18next';
import { useAppContext } from '@/app/providers/AppContextProvider';
import useMinimumLoading from '@/hooks/useMinimumLoading';
import type { ApplicationData, ProductTypeCoveragePayload } from '@/types/app';
import useProductQuotes from '../../hooks/useProductQuotes';
import Quote from '../cards/Quote';
import Carousel from './Carousel';

const SELECTION_TRANSITION_MS = 300;

interface InsuranceRenderContext {
	isBusy: boolean;
	refetch: (coverage?: ProductTypeCoveragePayload) => void;
	startLoading: () => void;
}

interface InsuranceProps {
	id: string;
	productType: string;
	applicationField: keyof ApplicationData;
	carouselKeyPrefix: string;
	sectionTranslationKey: string;
	productName: string;
	logLabel: string;
	initialCoverage?: ProductTypeCoveragePayload;
	extraBusy?: boolean;
	isSaving?: boolean;
	setIsSaving?: (saving: boolean) => void;
	onConfirm?: () => void;
	onBack?: () => void;
	onLoadingChange?: (isLoading: boolean) => void;
	onItemCountChange?: (itemCount: number) => void;
	showNavigation?: boolean;
	renderHeaderAction?: (context: InsuranceRenderContext) => React.ReactNode;
	desktopVisibleCards?: number;
}

const Insurance = ({
	id,
	productType,
	applicationField,
	carouselKeyPrefix,
	sectionTranslationKey,
	productName,
	logLabel,
	initialCoverage,
	extraBusy = false,
	isSaving = false,
	setIsSaving = () => {},
	onConfirm = () => {},
	onBack = () => {},
	onLoadingChange = () => {},
	onItemCountChange = () => {},
	showNavigation = true,
	renderHeaderAction,
	desktopVisibleCards
}: InsuranceProps): React.JSX.Element => {
	const { t } = useTranslation();
	const { applicationData, applicationManagement } = useAppContext();
	const [transitioningQuoteId, setTransitioningQuoteId] = useState<string | null>(null);

	const { quotes, expectedNumQuotes, isLoading, error, refetch, startLoading } = useProductQuotes(
		id,
		productType,
		initialCoverage
	);
	const isLoadingDisplayed = useMinimumLoading(isLoading);
	const isSectionLoading = isLoading || isLoadingDisplayed || extraBusy;

	const selectedQuoteId = applicationData[applicationField] as string | null;
	const isCardBusy = transitioningQuoteId !== null || isSaving || extraBusy;

	useEffect(() => {
		onLoadingChange(isSectionLoading);
	}, [isSectionLoading, onLoadingChange]);

	useEffect(() => {
		return () => onLoadingChange(false);
	}, [onLoadingChange]);

	useEffect(() => {
		// Only report item count when loading is done. During a re-fetch quotes are
		// momentarily empty; reporting 0 would lower desktopVisibleCards and cause
		// the skeleton row to shrink before new results arrive.
		if (!isLoading) {
			onItemCountChange(quotes.length);
		}
	}, [isLoading, quotes.length, onItemCountChange]);

	useEffect(() => {
		return () => onItemCountChange(0);
	}, [onItemCountChange]);

	async function handleQuoteToggle(quoteId: string): Promise<void> {
		if (isCardBusy) return;

		const previousSelected = selectedQuoteId;
		const isDeselecting = previousSelected === quoteId;
		const nextValue = isDeselecting ? null : quoteId;

		applicationManagement.update({ [applicationField]: nextValue });
		setTransitioningQuoteId(quoteId);

		try {
			await applicationManagement.save(id, { [applicationField]: nextValue });
			setTimeout(() => setTransitioningQuoteId(null), SELECTION_TRANSITION_MS);
		} catch (err) {
			console.error(`Error saving ${logLabel} selection:`, err);
			applicationManagement.update({ [applicationField]: previousSelected });
			setTransitioningQuoteId(null);
		}
	}

	async function handleSkipToggle(): Promise<void> {
		if (isCardBusy || selectedQuoteId === null) return;

		const previousSelected = selectedQuoteId;
		applicationManagement.update({ [applicationField]: null });

		try {
			await applicationManagement.save(id, { [applicationField]: null });
		} catch (err) {
			console.error(`Error clearing ${logLabel} selection:`, err);
			applicationManagement.update({ [applicationField]: previousSelected });
		}
	}

	async function handleConfirmOrSkip(): Promise<void> {
		const hasSelection = !!selectedQuoteId;

		setIsSaving(true);
		try {
			if (!hasSelection) {
				await applicationManagement.save(id, { [applicationField]: null });
				applicationManagement.update({ [applicationField]: null });
			}
			onConfirm();
		} catch (err) {
			console.error(`Error confirming ${logLabel}:`, err);
		} finally {
			setIsSaving(false);
		}
	}

	return (
		<Carousel
			title={t(`${sectionTranslationKey}.title`)}
			subtitle={
				<Trans i18nKey={`${sectionTranslationKey}.subtitle`} components={{ strong: <strong /> }} />
			}
			carouselKeyPrefix={carouselKeyPrefix}
			items={quotes}
			expectedNumQuotes={expectedNumQuotes}
			getItemId={(quote) => quote.quote_id}
			renderItem={(quote) => (
				<Quote
					quote={quote}
					productType={productType}
					isSelected={selectedQuoteId === quote.quote_id}
					isDisabled={isCardBusy}
					isTransitioning={transitioningQuoteId === quote.quote_id}
					onSelect={handleQuoteToggle}
				/>
			)}
			selectedId={selectedQuoteId}
			isBusy={isCardBusy}
			isSaving={isSaving}
			isLoading={isLoading}
			isLoadingDisplayed={isLoadingDisplayed}
			error={!!error}
			productName={productName}
			fetchErrorLabel={t(`${sectionTranslationKey}.fetchError`)}
			noItemsLabel={t(`${sectionTranslationKey}.noQuotesAvailable`)}
			confirmLabel={t(`${sectionTranslationKey}.confirmButton`)}
			skipLabel={t(`${sectionTranslationKey}.skipButton`)}
			onRetry={() => refetch(initialCoverage)}
			onSkip={handleSkipToggle}
			onConfirm={handleConfirmOrSkip}
			onBack={onBack}
			showNavigation={showNavigation}
			headerAction={renderHeaderAction?.({ isBusy: isCardBusy, refetch, startLoading })}
			placeholderLimits={{ base: 1, md: 2, lg: 3 }}
			desktopVisibleCards={desktopVisibleCards}
		/>
	);
};

export default Insurance;
